import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Minus, Plus, RotateCcw } from "lucide-react";
import { agruparPorBorda } from "../../../services/painelBordeiroService";

export type LinhaEditor = {
  bordaNome: string;
  bordaChave?: string;
  tamanho: string;
  tamanhoChave?: string;
  quantidadeBase: number;
  quantidadeSugerida: number;
  quantidadePlanejada: number;
};

type Props = {
  linhasIniciais: LinhaEditor[];
  percentual: number;
  dataBaseLabel: string;
  salvando: boolean;
  textoConfirmar: string;
  podeEditar: boolean;
  onConfirmar: (linhas: LinhaEditor[]) => void;
  onCancelar?: () => void;
};

function chaveLinha(l: LinhaEditor): string {
  return `${(l.bordaChave || l.bordaNome).toLowerCase()}|${(l.tamanhoChave || l.tamanho).toLowerCase()}`;
}

const EditorProducao: React.FC<Props> = ({
  linhasIniciais,
  percentual,
  dataBaseLabel,
  salvando,
  textoConfirmar,
  podeEditar,
  onConfirmar,
  onCancelar,
}) => {
  const [linhas, setLinhas] = useState<LinhaEditor[]>(linhasIniciais);

  useEffect(() => setLinhas(linhasIniciais), [linhasIniciais]);

  const alteradas = useMemo(
    () =>
      linhas.filter((l) => l.quantidadePlanejada !== l.quantidadeSugerida)
        .length,
    [linhas]
  );
  const totais = useMemo(
    () =>
      linhas.reduce(
        (acc, l) => ({
          base: acc.base + l.quantidadeBase,
          sugerida: acc.sugerida + l.quantidadeSugerida,
          planejada: acc.planejada + l.quantidadePlanejada,
        }),
        { base: 0, sugerida: 0, planejada: 0 }
      ),
    [linhas]
  );

  const grupos = useMemo(() => {
    const comChave = linhas.map((l) => ({
      ...l,
      bordaChave: l.bordaChave || l.bordaNome.toLowerCase(),
      bordaNome: l.bordaNome,
    }));
    return agruparPorBorda(comChave).filter((g) =>
      g.tamanhos.some(
        (t) =>
          t.quantidadeBase > 0 ||
          t.quantidadeSugerida > 0 ||
          t.quantidadePlanejada > 0
      )
    );
  }, [linhas]);

  const setPlanejada = (alvo: LinhaEditor, valor: number) => {
    const k = chaveLinha(alvo);
    setLinhas((prev) =>
      prev.map((l) =>
        chaveLinha(l) === k
          ? { ...l, quantidadePlanejada: Math.max(0, Math.floor(valor) || 0) }
          : l
      )
    );
  };

  const restaurarSugestao = () =>
    setLinhas((prev) =>
      prev.map((l) => ({ ...l, quantidadePlanejada: l.quantidadeSugerida }))
    );

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-600">
        Sugestão com base em {percentual}% do vendido em {dataBaseLabel}, por
        tamanho.
      </p>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {grupos.map((grupo) => {
          const totalGrupo = grupo.tamanhos.reduce(
            (s, t) => s + t.quantidadePlanejada,
            0
          );
          return (
            <div
              key={grupo.bordaChave}
              className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm"
            >
              <div className="flex items-center justify-between border-b border-neutral-100 bg-neutral-50 px-4 py-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
                    Borda
                  </p>
                  <h3 className="text-base font-bold text-neutral-900">
                    {grupo.bordaNome}
                  </h3>
                </div>
                <span className="rounded-full bg-primary/10 px-2.5 py-1 text-sm font-bold tabular-nums text-primary">
                  {totalGrupo} un.
                </span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 text-left text-xs text-neutral-500">
                    <th className="px-4 py-2 font-semibold">Tamanho</th>
                    <th className="px-2 py-2 text-right font-semibold">
                      Vendido
                    </th>
                    <th className="px-2 py-2 text-right font-semibold">
                      Sug. {percentual}%
                    </th>
                    <th className="px-4 py-2 text-right font-semibold">
                      Produzir
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50">
                  {grupo.tamanhos.map((t) => {
                    const diff = t.quantidadePlanejada - t.quantidadeSugerida;
                    return (
                      <tr key={`${grupo.bordaChave}-${t.tamanho}`}>
                        <td className="px-4 py-2.5 font-medium text-neutral-800">
                          {t.tamanho}
                        </td>
                        <td className="px-2 py-2.5 text-right tabular-nums text-neutral-500">
                          {t.quantidadeBase}
                        </td>
                        <td className="px-2 py-2.5 text-right tabular-nums text-neutral-700">
                          {t.quantidadeSugerida}
                        </td>
                        <td className="px-4 py-2.5">
                          {!podeEditar ? (
                            <p className="text-right text-base font-bold tabular-nums">
                              {t.quantidadePlanejada}
                            </p>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                onClick={() =>
                                  setPlanejada(t, t.quantidadePlanejada - 1)
                                }
                                className="rounded border border-neutral-300 p-1 text-neutral-600 hover:bg-neutral-100"
                                aria-label={`Diminuir ${t.tamanho}`}
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <input
                                type="number"
                                min={0}
                                value={t.quantidadePlanejada}
                                onChange={(e) =>
                                  setPlanejada(t, Number(e.target.value))
                                }
                                className={`w-14 rounded-md border px-1 py-1 text-center font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-primary ${
                                  diff !== 0
                                    ? "border-amber-400 bg-amber-50"
                                    : "border-neutral-300"
                                }`}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setPlanejada(t, t.quantidadePlanejada + 1)
                                }
                                className="rounded border border-neutral-300 p-1 text-neutral-600 hover:bg-neutral-100"
                                aria-label={`Aumentar ${t.tamanho}`}
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      {!podeEditar && (
        <p className="text-xs text-neutral-500">
          Somente Administrador ou Desenvolvedor pode alterar as quantidades.
        </p>
      )}

      <div className="flex flex-col gap-3 rounded-xl border border-neutral-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-neutral-700">
          Total a produzir:{" "}
          <span className="font-bold text-primary">{totais.planejada}</span>
          <span className="text-neutral-400">
            {" "}
            · sugestão {totais.sugerida} · vendido {totais.base}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {podeEditar && alteradas > 0 && (
            <button
              type="button"
              onClick={restaurarSugestao}
              className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Restaurar sugestão
            </button>
          )}
          {onCancelar && (
            <button
              type="button"
              onClick={onCancelar}
              disabled={salvando}
              className="rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              Cancelar
            </button>
          )}
          <button
            type="button"
            disabled={salvando || totais.planejada <= 0}
            onClick={() => onConfirmar(linhas)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50"
          >
            <CheckCircle2 className="h-4 w-4" />
            {textoConfirmar}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditorProducao;
